import React, {useMemo, useRef, useState} from 'react'

import IconButton from '../UI/buttons/IconButton.js'
import FieldBox, {FloatingLabel} from '../UI/forms/FieldBox.js'
import {fieldControlClass} from '../UI/forms/fieldStyles.js'
import {field, getFieldTextColor} from '../UI/forms/styles.js'
import {useFieldId} from '../UI/forms/utils.js'
import FieldMenu from './FieldMenu.js'
import {buildQuery, defaultOperator, fitOperator, getOperators, getSearchColumns, operators} from './query.js'
import {searchBar} from './styles.js'
import type {Operator, Query, SearchBarColumn, SearchBarColumns} from './types.js'

export interface SearchBoxProps {
    columns: SearchBarColumns
    /** A search was entered (Enter or the search button) */
    onAdd: (query: Query) => void
    /** Backspace in the empty field without a column: removes the last chip */
    onRemoveLast?: () => void
}

const iconButtonStyle: React.CSSProperties = {width: searchBar.iconButtonSize, height: searchBar.iconButtonSize, margin: 0}

/**
 * The search field: an optional column (filter button), an operator (Bevat, Is gelijk aan...) and the text. Enter or
 * the search button adds it as a query and empties the field. Backspace in the empty field takes off the column, then
 * the last chip.
 */
export default function SearchBox({columns, onAdd, onRemoveLast}: SearchBoxProps) {
    // The chosen column itself, not its index: the callers rebuild their columns (a branch column comes and goes)
    const [column, setColumn] = useState<SearchBarColumn | null>(null)
    const [operator, setOperator] = useState<Operator>(defaultOperator)
    const [text, setText] = useState('')
    const [inputFocused, setInputFocused] = useState(false)
    const [focusWithin, setFocusWithin] = useState(false)

    const inputId = useFieldId()
    const columnId = `${inputId}-column`
    const boxRef = useRef<HTMLDivElement>(null)
    const inputRef = useRef<HTMLInputElement>(null)

    const searchColumns = useMemo(() => getSearchColumns(columns), [columns])
    const columnOptions = useMemo(() => searchColumns.map((column, index) => ({value: String(index), label: column.title})), [searchColumns])
    const operatorOptions = getOperators(column).map((key) => ({
        value: key,
        label: operators[key].label,
        icon: operators[key].icon && `mdi ${operators[key].icon}`,
        symbol: key
    }))

    const focusInput = () => inputRef.current?.focus()

    const changeColumn = (newColumn: SearchBarColumn | null) => {
        setColumn(newColumn)
        // The whole row only supports some operators
        setOperator(fitOperator(operator, newColumn))
        focusInput()
    }

    const search = () => {
        const query = buildQuery(column, operator, text)

        if (query) {
            setColumn(null)
            setOperator(defaultOperator)
            setText('')
            onAdd(query)
        }
    }

    const onKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key === 'Enter') {
            event.preventDefault()
            search()
            return
        }

        // Only a single press: holding Backspace while deleting text must not go on to remove the chips
        if (event.key === 'Backspace' && !text && !event.repeat) {
            if (column) {
                changeColumn(null)
            } else {
                onRemoveLast?.()
            }
        }
    }

    const operatorIcon = operators[operator].icon

    return (
        <FieldBox
            focused={focusWithin}
            style={{maxWidth: searchBar.searchMaxWidth, flexShrink: 1}}
            boxStyle={{paddingLeft: searchBar.boxPaddingX, paddingRight: searchBar.boxPaddingX}}
            boxRef={boxRef}
            boxProps={{
                onFocus: () => setFocusWithin(true),
                onBlur: (event) => {
                    if (!event.currentTarget.contains(event.relatedTarget as Node)) {
                        setFocusWithin(false)
                    }
                }
            }}
        >
            {column ?
                <IconButton
                    icon='mdi mdi-filter-variant-remove'
                    tooltip='Kolom wissen'
                    style={iconButtonStyle}
                    onClick={() => changeColumn(null)}
                /> :
                <FieldMenu
                    label='Kolom kiezen'
                    icon='mdi mdi-filter-variant-plus'
                    options={columnOptions}
                    anchorRef={boxRef}
                    onSelect={(index) => changeColumn(searchColumns[Number(index)] || null)}
                />
            }

            <span
                id={columnId}
                style={{
                    fontSize: field.fontSize,
                    color: getFieldTextColor({}),
                    marginLeft: column ? 6 : 1,
                    marginRight: column ? 6 : 1,
                    flexShrink: column ? 1 : 0,
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden'
                }}
            >
                {column?.title}
            </span>

            <FieldMenu
                label={`Zoekmethode: ${operators[operator].label}`}
                icon={operatorIcon && `mdi ${operatorIcon}`}
                options={operatorOptions}
                value={operator}
                anchorRef={boxRef}
                onSelect={(value) => {
                    setOperator(value as Operator)
                    focusInput()
                }}
            >
                {!operatorIcon && <span style={{fontSize: field.fontSize}}>{operator}</span>}
            </FieldMenu>

            {/* The label sits above the text, after the buttons, so it is positioned against this wrapper */}
            <div style={{position: 'relative', flex: 3, alignSelf: 'stretch', minWidth: 0, marginLeft: 8, marginRight: 8}}>
                <FloatingLabel label='Zoeken' floated={inputFocused || !!text} paddingLeft={0} htmlFor={inputId} />

                <input
                    ref={inputRef}
                    id={inputId}
                    className={fieldControlClass}
                    value={text}
                    autoComplete='off'
                    aria-describedby={column ? columnId : undefined}
                    style={{
                        fontSize: field.fontSize,
                        width: '100%',
                        height: '100%',
                        minWidth: 0,
                        padding: 0,
                        paddingTop: field.paddingTop,
                        paddingBottom: field.paddingBottom,
                        outline: 'none',
                        border: 'none',
                        background: 'transparent',
                        color: getFieldTextColor({})
                    }}
                    onChange={(event) => setText(event.target.value)}
                    onFocus={() => setInputFocused(true)}
                    onBlur={() => setInputFocused(false)}
                    onKeyDown={onKeyDown}
                />
            </div>

            <IconButton
                icon='mdi mdi-magnify'
                tooltip='Zoeken'
                style={iconButtonStyle}
                onClick={search}
            />
        </FieldBox>
    )
}
